"use client";

import { useMemo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import UpcomingAppointments from "../UpcomingAppointments/UpcomingAppointments";
import PastAppointments from "../PastAppointments/PastAppointments";
import PatientRescheduleModal from "../PatientRescheduleModal/PatientRescheduleModal";

import useRendezvous from "../../hooks/useRendezvous";

import "./AppointmentTabs.css";

const AppointmentTabs = ({ doctors = [], initialTab, highlightId }) => {
  const { t, i18n } = useTranslation();

  const [activeTab, setActiveTab] = useState(
    initialTab === "history" ? "history" : "upcoming"
  );

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

  const { rescheduleAppointment } = useRendezvous();

  useEffect(() => {
    if (initialTab === "upcoming" || initialTab === "history") {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const isRTL = i18n.language?.startsWith("ar");

  useEffect(() => {
    const lang = i18n.language || "en";
    document.documentElement.dir = lang.startsWith("ar") ? "rtl" : "ltr";
  }, [i18n.language]);

  const headerText = useMemo(() => {
    return activeTab === "upcoming"
      ? t("appointmentss.tabs.headerUpcoming")
      : t("appointmentss.tabs.headerHistory");
  }, [activeTab, t]);

  const subText = useMemo(() => {
    return activeTab === "upcoming"
      ? t("appointmentss.tabs.subUpcoming")
      : t("appointmentss.tabs.subHistory");
  }, [activeTab, t]);

  const handleOpenRescheduleModal = useCallback((appointment) => {

    setSelectedAppointment(appointment);
    setShowRescheduleModal(true);
  }, []);

  const handleCloseRescheduleModal = useCallback(() => {
    setSelectedAppointment(null);
    setShowRescheduleModal(false);
  }, []);

  const handleConfirmPatientReschedule = useCallback(
    async ({ appointment, startTime, endTime }) => {

      await rescheduleAppointment({
        appointment,
        startTime,
        endTime,
        by: "patient",
      });

      handleCloseRescheduleModal();
      setRefreshKey((prev) => prev + 1);
    },
    [rescheduleAppointment, handleCloseRescheduleModal]
  );

  return (
    <div className={`appointment-tabs-container${isRTL ? " is-rtl" : ""}`}>
      <div
        className="tab-toggle"
        role="tablist"
        aria-label={t("appointmentss.tabs.ariaLabel")}
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "upcoming"}
          className={`tab-pill${activeTab === "upcoming" ? " active" : ""}`}
          onClick={() => setActiveTab("upcoming")}
        >
          <span className="tab-icon">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </span>

          {t("appointmentss.tabs.upcoming")}
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "history"}
          className={`tab-pill${activeTab === "history" ? " active" : ""}`}
          onClick={() => setActiveTab("history")}
        >
          <span className="tab-icon">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="12 8 12 12 14 14" />
              <path d="M3.05 11a9 9 0 1 1 .5 4m-.5 5v-5h5" />
            </svg>
          </span>

          {t("appointmentss.tabs.history")}
        </button>
      </div>

      <div className="tab-content">
        {activeTab === "upcoming" ? (
          <UpcomingAppointments
            doctors={doctors}
            highlightId={highlightId}
            refreshKey={refreshKey}
            onOpenReschedule={handleOpenRescheduleModal}
            onAppointmentsChanged={() => setRefreshKey((prev) => prev + 1)}
          />
        ) : (
          <PastAppointments />
        )}
      </div>

      {showRescheduleModal && selectedAppointment && (
        <PatientRescheduleModal
          appointment={selectedAppointment}
          onClose={handleCloseRescheduleModal}
          onConfirm={handleConfirmPatientReschedule}
        />
      )}
    </div>
  );
};

AppointmentTabs.propTypes = {
  doctors: PropTypes.array,
  initialTab: PropTypes.string,
  highlightId: PropTypes.string,
};

export default AppointmentTabs;